import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';
import { generateCSV } from '../../utils/token';
import ProspectsHeader from '../../components/admin/ProspectsHeader';
import ProspectsTable from '../../components/admin/ProspectsTableOptimized';
import AddProspectModal from '../../components/admin/AddProspectModal';
import { getUrl } from '../../config/urls';
import { useLocation } from 'react-router-dom';
import { CheckCircle, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../lib/auth';

interface Prospect {
  id: string;
  prenom: string;
  nom: string;
  email: string;
  token: string;
  page_vue: boolean;
  quiz_commence: boolean;
  quiz_en_cours: boolean;
  quiz_fait: boolean;
  score: number | null;
  rdv_pris: boolean;
  created_at: string;
  reponses_count?: number; // Nombre de réponses données au quiz
}

const ProspectsList: React.FC = () => {
  const location = useLocation();
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState(''); // Added for search
  const [currentPage, setCurrentPage] = useState(1);
  const [prospectsPerPage] = useState(10);
  
  // État pour le tri
  const [sortColumn, setSortColumn] = useState(() => {
    const savedSort = localStorage.getItem('prospectsListSortColumn');
    return savedSort || 'created_at';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    const savedDirection = localStorage.getItem('prospectsListSortDirection');
    return savedDirection || 'desc';
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState<string | null>(null);
  const [newProspect, setNewProspect] = useState({
    prenom: '',
    nom: '',
    email: '',
    linkedin: '',
    site_url: ''
  });

  // Effet pour vérifier si nous arrivons depuis une page de suppression
  useEffect(() => {
    // Si nous avons un state de navigation avec deleteSuccess, déclencher le rafraîchissement
    if (location.state && location.state.deleteSuccess) {
      setRefreshTrigger(prev => prev + 1);
      
      // Afficher le message de succès
      const deletedProspectName = location.state.deletedProspectName || 'Le prospect';
      setDeleteSuccessMessage(`${deletedProspectName} a été supprimé avec succès`);
      
      // Masquer le message après 5 secondes
      setTimeout(() => {
        setDeleteSuccessMessage(null);
      }, 5000);
      
      // Effacer l'état de navigation pour éviter des rafraîchissements en boucle
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  // État pour les erreurs
  const [error, setError] = useState<string | null>(null);

  // État pour le nombre total de prospects (pour la pagination)
  const [totalProspectsCount, setTotalProspectsCount] = useState(0);
  
  // Sauvegarder les préférences de tri dans localStorage
  useEffect(() => {
    localStorage.setItem('prospectsListSortColumn', sortColumn);
    localStorage.setItem('prospectsListSortDirection', sortDirection);
  }, [sortColumn, sortDirection]);
  
  // Fonction de récupération des prospects optimisée avec useCallback
  const fetchProspects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Construire la requête de base
      let query = supabase
        .from('prospects')
        .select(`
          *,
          reponses_quiz(id, token)`, { count: 'exact' })
        .order(sortColumn, { ascending: sortDirection === 'asc' });
      
      // Appliquer les filtres côté serveur
      if (filter === 'viewed') {
        query = query.eq('page_vue', true);
      } else if (filter === 'quiz') {
        query = query.eq('quiz_fait', true);
      } else if (filter === 'highscore') {
        query = query.gt('score', 70);
      }
      
      // Appliquer la recherche côté serveur si un terme de recherche est défini
      if (searchTerm) {
        query = query.or(`prenom.ilike.%${searchTerm}%,nom.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }
      
      // Calculer les indices pour la pagination côté serveur
      const from = (currentPage - 1) * prospectsPerPage;
      const to = from + prospectsPerPage - 1;
      
      // Exécuter la requête avec pagination
      const { data: prospectsData, error, count } = await query
        .range(from, to);
      
      if (import.meta.env.DEV) {
      }
      
      if (error) {
        if (import.meta.env.DEV) console.error('Erreur lors de la récupération des prospects:', error);
        setError(`Erreur de récupération: ${error.message}`);
        return;
      }
      
      if (!prospectsData || prospectsData.length === 0) {
        setProspects([]);
        setTotalProspectsCount(0);
        return;
      }
      
      // Transformer les données pour obtenir le nombre de réponses
      const processedProspects = prospectsData.map(prospect => {
        // Compter les réponses au quiz en utilisant les données jointes
        const reponses_count = prospect.reponses_quiz ? prospect.reponses_quiz.length : 0;
        
        // Supprimer les données jointes de l'objet final pour le rendre compatible avec l'interface Prospect
        const { reponses_quiz, ...prospectData } = prospect;
        
        return { ...prospectData, reponses_count };
      });
      
      if (import.meta.env.DEV) {
      }
      
      setProspects(processedProspects);
      setTotalProspectsCount(count || 0);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      if (import.meta.env.DEV) console.error('Erreur lors du chargement des prospects:', error);
      setError(`Exception: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [filter, searchTerm, currentPage, prospectsPerPage, sortColumn, sortDirection]);

  // Effet pour déclencher le chargement des prospects
  useEffect(() => {
    fetchProspects();
  }, [fetchProspects, refreshTrigger]);

  // Les prospects sont déjà filtrés et paginés côté serveur
  const currentProspects = prospects;
  
  // Calcul des indices pour l'affichage
  const indexOfFirstProspect = (currentPage - 1) * prospectsPerPage + 1;
  const indexOfLastProspect = Math.min(currentPage * prospectsPerPage, totalProspectsCount);
  
  // Calcul du nombre total de pages
  const totalPages = Math.ceil(totalProspectsCount / prospectsPerPage);
  
  // Fonctions de pagination avec rechargement des données
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // fetchProspects sera appelé automatiquement via la dépendance dans useEffect
  };
  
  const previousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
    // fetchProspects sera appelé automatiquement via la dépendance dans useEffect
  };
  
  const nextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
    // fetchProspects sera appelé automatiquement via la dépendance dans useEffect
  };
  
  // Fonction pour changer le tri
  const handleSort = (column: string) => {
    // Si on clique sur la même colonne, on inverse la direction
    if (column === sortColumn) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Sinon, on change la colonne et on met la direction par défaut
      setSortColumn(column);
      setSortDirection('desc');
    }
    // Retour à la première page lors du changement de tri
    setCurrentPage(1);
  };

  const copyLink = (token: string) => {
    const link = `${window.location.origin}${getUrl('', token)}`;
    navigator.clipboard.writeText(link);
  };

  const exportCSV = () => {
    const csvContent = generateCSV(prospects);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prospects.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleAddProspect = async () => {
    try {
      // Validation côté client avant envoi
      if (!newProspect.prenom || !newProspect.nom || !newProspect.email) {
        setError("Veuillez remplir tous les champs obligatoires");
        return;
      }
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/prospects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newProspect),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Échec de la création du prospect');
      }

      // La réponse est OK, le prospect a été créé avec succès
      // Rafraîchir les données au lieu de mettre à jour l'état manuellement
      setRefreshTrigger(prev => prev + 1);
      setShowAddModal(false);
      setNewProspect({ prenom: '', nom: '', email: '', linkedin: '', site_url: '' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      if (import.meta.env.DEV) console.error('Error creating prospect:', error);
      setError(`Erreur: ${errorMessage}`);
    }
  };

  const handleProspectChange = (field: keyof typeof newProspect, value: string) => {
    setNewProspect(prev => ({ ...prev, [field]: value }));
  };

  // Calculate statistics
  const viewedCount = prospects.filter(p => p.page_vue).length;
  const quizCompletedCount = prospects.filter(p => p.quiz_fait).length;
  const highScoreCount = prospects.filter(p => p.score && p.score > 70).length;
  const appointmentCount = prospects.filter(p => p.rdv_pris).length;

  // Utiliser le hook d'authentification
  const { isAuthenticated } = useAuth();

  return (
    <AdminLayout>
      {/* Message de succès de suppression */}
      {deleteSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg max-w-md">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">
                Suppression réussie
              </p>
              <p className="text-sm text-green-700 mt-1">
                {deleteSuccessMessage}
              </p>
            </div>
            <button
              onClick={() => setDeleteSuccessMessage(null)}
              className="ml-4 text-green-400 hover:text-green-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {error && (
        <div className="flex items-center p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50">
          <AlertTriangle className="w-5 h-5 mr-2" />
          <span>Erreur de chargement des données: {error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {!isAuthenticated && (
        <div className="flex items-center p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50">
          <AlertTriangle className="w-5 h-5 mr-2" />
          <span>Vous n'êtes pas authentifié. Certaines fonctionnalités peuvent être limitées.</span>
        </div>
      )}
      
      <div className="p-8 bg-gradient-to-b from-blue-50 via-white to-indigo-50 min-h-screen space-y-6">
        <div className="max-w-7xl mx-auto">
          {/* Header with statistics and BulkScan */}
          <ProspectsHeader 
            prospectsCount={totalProspectsCount}
            viewedCount={viewedCount}
            quizCompletedCount={quizCompletedCount}
            highScoreCount={highScoreCount}
            appointmentCount={appointmentCount}
            onAddClick={() => setShowAddModal(true)}
            onExportClick={exportCSV}
            onScanComplete={() => {
              // Rafraîchir la liste des prospects après le scan
              setRefreshTrigger(prev => prev + 1);
            }}
          />

          {/* Filter and Search Section */}
          <div className="mb-6 p-4 bg-white rounded-lg shadow">
            <div className="grid md:grid-cols-2 gap-4 items-center">
              {/* Search Input */}
              <div>
                <input
                  type="text"
                  placeholder="Rechercher par nom, prénom, email..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-3 justify-start md:justify-end">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                    filter === 'all' 
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Tous les prospects
                </button>
                <button
                  onClick={() => setFilter('viewed')}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                    filter === 'viewed' 
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${filter === 'viewed' ? 'bg-white' : 'bg-green-500'}`}></span>
                    Pages vues
                  </div>
                </button>
                <button
                  onClick={() => setFilter('quiz')}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                    filter === 'quiz' 
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${filter === 'quiz' ? 'bg-white' : 'bg-green-500'}`}></span>
                    Quiz terminés
                  </div>
                </button>
                <button
                  onClick={() => setFilter('highscore')}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                    filter === 'highscore' 
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${filter === 'highscore' ? 'bg-white' : 'bg-purple-500'}`}></span>
                    Score élevé
                  </div>
                </button>
              </div> {/* End of flex flex-wrap for buttons */}
            </div> {/* End of grid md:grid-cols-2 */}
          </div>
          
          {/* Prospects table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
            <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <h2 className="text-xl font-bold text-blue-800">Liste des prospects ({totalProspectsCount})</h2>
            </div>
            {isLoading ? (
              <div className="py-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>
            ) : prospects.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-8 text-center">
                <p className="text-gray-500 text-lg">Aucun prospect trouvé</p>
              </div>
            ) : (
              <ProspectsTable 
                prospects={currentProspects}
                currentPage={currentPage}
                totalPages={totalPages}
                onCopyLink={copyLink}
                onPreviousPage={previousPage}
                onNextPage={nextPage}
                onPageChange={paginate}
                indexOfFirstProspect={indexOfFirstProspect}
                indexOfLastProspect={indexOfLastProspect}
                totalProspects={totalProspectsCount}
                onSort={handleSort}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
              />
            )}
          </div>
          
          {/* Add prospect modal */}
          <AddProspectModal 
            isOpen={showAddModal}
            prospect={newProspect}
            onClose={() => setShowAddModal(false)}
            onSave={handleAddProspect}
            onChange={handleProspectChange}
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default ProspectsList;
